import { buttonVariants } from "@platform/ui/components";
import { copy, heading } from "@platform/ui/styles";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import * as React from "react";
import { twMerge } from "tailwind-merge";
import { VariantProps } from "tailwind-variants";

export const AlertDialog = AlertDialogPrimitive.Root;

export const AlertDialogTrigger = AlertDialogPrimitive.Trigger;

const AlertDialogPortal = AlertDialogPrimitive.Portal;

export const AlertDialogOverlay = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({ className, ...rest }, ref) => {
  return (
    <AlertDialogPrimitive.Overlay
      className={twMerge(
        "bg-ds-scrim data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50",
        className,
      )}
      {...rest}
      ref={ref}
    />
  );
});
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;

export const AlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>
>(({ className, ...rest }, ref) => {
  return (
    <AlertDialogPortal>
      <AlertDialogOverlay>
        <AlertDialogPrimitive.Content
          ref={ref}
          className={twMerge(
            [
              "border-ds-stroke-primary bg-ds-surface-overlay fixed top-1/2 left-1/2 z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-6 border p-6 shadow-lg sm:rounded-lg",
              "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
              "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
              "duration-200",
            ],
            className,
          )}
          {...rest}
        />
      </AlertDialogOverlay>
    </AlertDialogPortal>
  );
});
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;

export function AlertDialogHeader({ className, ...rest }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={twMerge("flex flex-col gap-2 text-center sm:text-left", className)} {...rest} />;
}
AlertDialogHeader.displayName = "AlertDialogHeader";

export function AlertDialogFooter({ className, ...rest }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={twMerge("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)} {...rest} />;
}
AlertDialogFooter.displayName = "AlertDialogFooter";

export const AlertDialogTitle = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(({ className, ...rest }, ref) => {
  return <AlertDialogPrimitive.Title ref={ref} className={heading({ variant: "24", className })} {...rest} />;
});
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;

export const AlertDialogDescription = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(({ className, ...rest }, ref) => {
  return (
    <AlertDialogPrimitive.Description
      ref={ref}
      className={copy({
        variant: "14",
        color: "primary",
        className,
      })}
      {...rest}
    />
  );
});
AlertDialogDescription.displayName = AlertDialogPrimitive.Description.displayName;

export const AlertDialogAction = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action> & VariantProps<typeof buttonVariants>
>(({ className, variant, size, svgOnly, align, isLoading, ...rest }, ref) => {
  const { base } = buttonVariants({
    variant,
    size,
    svgOnly,
    align,
    isLoading,
  });

  return (
    <AlertDialogPrimitive.Action
      ref={ref}
      className={base({ variant: variant ?? "destructive", className })}
      {...rest}
    />
  );
});
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;

export const AlertDialogCancel = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel> & VariantProps<typeof buttonVariants>
>(({ className, variant, size, svgOnly, align, isLoading, ...rest }, ref) => {
  const { base } = buttonVariants({
    variant,
    size,
    svgOnly,
    align,
    isLoading,
  });

  return (
    <AlertDialogPrimitive.Cancel ref={ref} className={base({ variant: variant ?? "outline", className })} {...rest} />
  );
});
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;
